"use client";

import React from "react";
import { NavCart } from "dds/organisms/global-nav/nav-cart.client";
import type { CartCmsSource } from "dds/molecules/cart-side-sheet/use-cart-cms";

const STORYBLOK_CDN_ORIGIN = "https://api-us.storyblok.com";

/* Module scope, so its identity is stable. The parser it pulls in is only
   needed once the sheet opens, which most visits never do, so the chunk is
   requested on intent (`warmCartCms`) or, failing that, on the first fetch. */
const loadFetchCartCms = () => import("./fetch-cart-cms");

const lazyFetchCartCms: CartCmsSource["fetch"] = async (slug, token, cachedVersion) => {
  const { fetchCartCms } = await loadFetchCartCms();
  return fetchCartCms(slug, token, cachedVersion);
};

/* Runs once per page, when the shopper first points at the cart: DNS + TLS to
   Storyblok and the parser chunk, in parallel with the sheet's own chunk. */
let warmed = false;
export const warmCartCms = () => {
  if (warmed) {
    return;
  }
  warmed = true;
  const link = document.createElement("link");
  link.rel = "preconnect";
  link.href = STORYBLOK_CDN_ORIGIN;
  link.crossOrigin = "anonymous";
  document.head.appendChild(link);
  /* A failed preload is not an error: the first fetch requests it again. */
  loadFetchCartCms().catch(() => undefined);
};

/** Test-only: lets each spec start from a cold page. */
export const __resetWarmCartCms = () => {
  warmed = false;
};

export interface V3NavCartProps {
  /** Must match the `cart.panelId` handed to GlobalNav. */
  panelId: string;
  /** The on-screen eyebrow (and accessible name) until the CMS panel title
   *  loads, then its fallback. */
  eyebrowLabel: string;
  /** Storyblok delivery token. Absent → no fetch; the sheet renders its default copy. */
  token?: string;
  /** Storyblok cache version. Absent → the request is sent without `cv`. */
  cachedVersion?: string;
}

/**
 * Supplies NavCart's Storyblok fetcher. The v3 layout is a Server Component
 * and cannot pass a function prop, so the fetcher is bound here instead.
 */
export function V3NavCart({ panelId, eyebrowLabel, token, cachedVersion }: V3NavCartProps) {
  return (
    <NavCart
      eyebrowLabel={eyebrowLabel}
      cartCms={{
        token,
        cachedVersion,
        /* No token means the request can only fail. Without a fetcher the
           sheet goes straight to its default copy instead of a failed load
           on every open — and there is nothing to warm. */
        fetch: token ? lazyFetchCartCms : undefined,
        warm: token ? warmCartCms : undefined
      }}
      panelId={panelId}
    />
  );
}

export default V3NavCart;
